import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';

// ─── Global reset for standalone widget use ──────────────────────────────────

const WidgetGlobal = createGlobalStyle`
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; background: #f5f5f5; }
`;

// ─── Tokens ───────────────────────────────────────────────────────────────────

const brand = (config) => config?.brandColors?.primary || '#111111';

// ─── Layout ───────────────────────────────────────────────────────────────────

const Shell = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  padding: 24px 16px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 32px rgba(0,0,0,0.10);
  width: 100%;
  max-width: 480px;
  overflow: hidden;
`;

const CardHeader = styled.div`
  background: ${p => p.primary};
  padding: 28px 28px 20px;
  color: #fff;
`;

const Logo = styled.img`
  height: 36px;
  margin-bottom: 10px;
  display: block;
`;

const VenueName = styled.h1`
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.5px;
`;

const CardBody = styled.div`
  padding: 28px;
`;

// ─── Step indicator ───────────────────────────────────────────────────────────

const StepRow = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 24px;
`;

const StepDot = styled.div`
  flex: 1;
  height: 3px;
  border-radius: 3px;
  background: ${p => p.active ? p.primary : '#e0e0e0'};
  transition: background 0.2s;
`;

// ─── Typography ───────────────────────────────────────────────────────────────

const SectionTitle = styled.h2`
  font-size: 17px;
  font-weight: 700;
  color: #111;
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
`;

// ─── Inputs ───────────────────────────────────────────────────────────────────

const Input = styled.input`
  width: 100%;
  padding: 11px 14px;
  border: 1.5px solid #ddd;
  border-radius: 8px;
  font-size: 15px;
  color: #111;
  outline: none;
  transition: border-color 0.15s;
  &:focus { border-color: ${p => p.primary}; }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 11px 14px;
  border: 1.5px solid #ddd;
  border-radius: 8px;
  font-size: 15px;
  color: #111;
  outline: none;
  resize: none;
  transition: border-color 0.15s;
  &:focus { border-color: ${p => p.primary}; }
`;

const FieldGroup = styled.div`
  margin-bottom: 14px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

// ─── Buttons ─────────────────────────────────────────────────────────────────

const Btn = styled.button`
  padding: 13px 20px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.1s;
  &:active { transform: scale(0.98); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const PrimaryBtn = styled(Btn)`
  background: ${p => p.primary};
  color: #fff;
  width: 100%;
  &:hover:not(:disabled) { opacity: 0.88; }
`;

const GhostBtn = styled(Btn)`
  background: transparent;
  color: #666;
  padding: 10px 0;
  font-size: 14px;
  &:hover { color: #111; }
`;

const BtnRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
`;

// ─── Date step ────────────────────────────────────────────────────────────────

const DateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
  margin-bottom: 20px;
`;

const DateCell = styled.button`
  aspect-ratio: 1;
  border-radius: 8px;
  border: 1.5px solid ${p => p.selected ? p.primary : '#e0e0e0'};
  background: ${p => p.selected ? p.primary : '#fff'};
  color: ${p => p.selected ? '#fff' : '#111'};
  font-size: 13px;
  font-weight: ${p => p.selected ? 700 : 400};
  cursor: pointer;
  transition: all 0.12s;
  &:hover { border-color: ${p => p.primary}; }
  ${p => p.past && `opacity: 0.35; cursor: default;`}
`;

const DayLabel = styled.div`
  text-align: center;
  font-size: 11px;
  color: #999;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 4px;
`;

const PartyRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8f8f8;
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 20px;
`;

const Counter = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const CountBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1.5px solid #ddd;
  background: #fff;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #111;
  &:hover { border-color: ${p => p.primary}; color: ${p => p.primary}; }
  &:disabled { opacity: 0.3; cursor: default; }
`;

// ─── Time step ────────────────────────────────────────────────────────────────

const TimeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
`;

const TimeSlot = styled.button`
  padding: 12px 8px;
  border-radius: 10px;
  border: 1.5px solid ${p => p.selected ? p.primary : p.available ? '#ddd' : '#f0f0f0'};
  background: ${p => p.selected ? p.primary : '#fff'};
  color: ${p => p.selected ? '#fff' : p.available ? '#111' : '#ccc'};
  font-size: 14px;
  font-weight: ${p => p.selected ? 700 : 500};
  cursor: ${p => p.available ? 'pointer' : 'default'};
  transition: all 0.12s;
  &:hover:not(:disabled) { border-color: ${p => p.primary}; }
`;

const Sub = styled.div`
  font-size: 10px;
  opacity: 0.75;
  margin-top: 2px;
`;

const EmptySlots = styled.div`
  text-align: center;
  padding: 32px 0;
  color: #999;
  font-size: 14px;
`;

// ─── Confirmation ─────────────────────────────────────────────────────────────

const ConfirmBox = styled.div`
  background: #f0faf0;
  border: 1.5px solid #b2dfb2;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
`;

const ConfirmCheck = styled.div`
  font-size: 40px;
  text-align: center;
  margin-bottom: 12px;
`;

const ConfirmTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 4px;
`;

const ConfirmSub = styled.p`
  text-align: center;
  color: #666;
  font-size: 14px;
  margin-bottom: 16px;
`;

const ConfirmDetail = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  padding: 6px 0;
  border-bottom: 1px solid #e0e0e0;
  &:last-child { border-bottom: none; }
`;

const ConfirmLabel = styled.span`
  color: #666;
`;

const ConfirmValue = styled.span`
  font-weight: 600;
  color: #111;
`;

const ErrorMsg = styled.div`
  background: #fff0f0;
  border: 1px solid #fbb;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #c00;
  margin-bottom: 14px;
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  return `${hour > 12 ? hour - 12 : hour || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
};

const fmtDate = (iso) => {
  try {
    return new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  } catch { return iso; }
};

const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const buildCalendarDays = (baseDate, count = 14) => {
  const days = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(baseDate + 'T12:00:00');
    d.setDate(d.getDate() + i);
    days.push(d.toISOString().slice(0, 10));
  }
  return days;
};

// ─── Component ────────────────────────────────────────────────────────────────

const STEPS = { DATE: 'date', TIME: 'time', DETAILS: 'details', CONFIRM: 'confirm' };
const STEP_IDX = { date: 0, time: 1, details: 2, confirm: 3 };

const BookingWidgetView = ({
  config,
  step,
  date,
  partySize,
  slots,
  selectedTime,
  form,
  loading,
  slotsLoading,
  confirmation,
  error,
  addDays,
  onDateChange,
  onPartySizeChange,
  onDateNext,
  onBack,
  onTimeSelect,
  onFormChange,
  onSubmit,
  onReset,
}) => {
  const primary = brand(config);
  const todayIso = new Date().toISOString().slice(0, 10);
  const calDays = buildCalendarDays(todayIso, 14);
  const stepIdx = STEP_IDX[step];

  if (!config) {
    return (
      <Shell>
        <Card>
          <CardBody>
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>Loading...</div>
          </CardBody>
        </Card>
      </Shell>
    );
  }

  return (
    <>
      <WidgetGlobal />
      <Shell>
        <Card>
          {/* Header */}
          <CardHeader primary={primary}>
            {config.logoUrl && <Logo src={config.logoUrl} alt={config.name} />}
            <VenueName>{config.name}</VenueName>
            <div style={{ fontSize: 13, opacity: 0.8, marginTop: 4 }}>Reserve a table</div>
          </CardHeader>

          <CardBody>
            {/* Step dots */}
            {step !== STEPS.CONFIRM && (
              <StepRow>
                {[0, 1, 2].map(i => (
                  <StepDot key={i} active={i <= stepIdx} primary={primary} />
                ))}
              </StepRow>
            )}

            {/* ── STEP 1: Date + Party Size ─────────────────────────────── */}
            {step === STEPS.DATE && (
              <>
                <SectionTitle>When would you like to visit?</SectionTitle>

                <Label>Party Size</Label>
                <PartyRow>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>Number of guests</span>
                  <Counter>
                    <CountBtn primary={primary} onClick={() => onPartySizeChange(Math.max(1, partySize - 1))} disabled={partySize <= 1}>-</CountBtn>
                    <span style={{ fontSize: 18, fontWeight: 700, minWidth: 20, textAlign: 'center' }}>{partySize}</span>
                    <CountBtn primary={primary} onClick={() => onPartySizeChange(Math.min(20, partySize + 1))} disabled={partySize >= 20}>+</CountBtn>
                  </Counter>
                </PartyRow>

                <Label>Date</Label>
                <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                  {DAYS.map(d => <DayLabel key={d} style={{ flex: 1 }}>{d}</DayLabel>)}
                </div>
                <DateGrid>
                  {calDays.map(d => {
                    const past = d < todayIso;
                    return (
                      <DateCell
                        key={d}
                        primary={primary}
                        selected={d === date}
                        past={past}
                        onClick={() => !past && onDateChange(d)}
                      >
                        {parseInt(d.slice(8), 10)}
                      </DateCell>
                    );
                  })}
                </DateGrid>
                <div style={{ fontSize: 12, color: '#999', marginBottom: 20 }}>
                  {fmtDate(date)}
                </div>

                <PrimaryBtn primary={primary} onClick={onDateNext}>
                  See Available Times
                </PrimaryBtn>
              </>
            )}

            {/* ── STEP 2: Time ──────────────────────────────────────────── */}
            {step === STEPS.TIME && (
              <>
                <SectionTitle>
                  Choose a time
                  <div style={{ fontSize: 13, fontWeight: 400, color: '#666', marginTop: 2 }}>
                    {fmtDate(date)} &middot; {partySize} guest{partySize !== 1 ? 's' : ''}
                  </div>
                </SectionTitle>

                {slotsLoading ? (
                  <EmptySlots>Checking availability...</EmptySlots>
                ) : slots.length === 0 ? (
                  <EmptySlots>
                    No availability for this date.<br />
                    <span style={{ fontSize: 12 }}>Try a different date or party size.</span>
                  </EmptySlots>
                ) : (
                  <TimeGrid>
                    {slots.map(slot => (
                      <TimeSlot
                        key={slot.time}
                        primary={primary}
                        available={slot.available}
                        selected={selectedTime === slot.time}
                        disabled={!slot.available}
                        onClick={() => slot.available && onTimeSelect(slot.time)}
                      >
                        {fmtTime(slot.time)}
                        {slot.available && slot.remaining_covers <= 6 && (
                          <Sub>{slot.remaining_covers} left</Sub>
                        )}
                        {!slot.available && <Sub>Full</Sub>}
                      </TimeSlot>
                    ))}
                  </TimeGrid>
                )}

                <BtnRow>
                  <GhostBtn onClick={onBack}>Back</GhostBtn>
                  <PrimaryBtn
                    primary={primary}
                    style={{ width: 'auto', paddingLeft: 28, paddingRight: 28 }}
                    onClick={() => selectedTime && onTimeSelect(selectedTime)}
                    disabled={!selectedTime}
                  >
                    Continue
                  </PrimaryBtn>
                </BtnRow>
              </>
            )}

            {/* ── STEP 3: Guest Details ─────────────────────────────────── */}
            {step === STEPS.DETAILS && (
              <>
                <SectionTitle>
                  Your details
                  <div style={{ fontSize: 13, fontWeight: 400, color: '#666', marginTop: 2 }}>
                    {fmtDate(date)} at {fmtTime(selectedTime)} &middot; {partySize} guest{partySize !== 1 ? 's' : ''}
                  </div>
                </SectionTitle>

                {error && <ErrorMsg>{error}</ErrorMsg>}

                <Row>
                  <FieldGroup>
                    <Label>First Name *</Label>
                    <Input primary={primary} value={form.first_name} onChange={e => onFormChange('first_name', e.target.value)} placeholder="Jane" />
                  </FieldGroup>
                  <FieldGroup>
                    <Label>Last Name *</Label>
                    <Input primary={primary} value={form.last_name} onChange={e => onFormChange('last_name', e.target.value)} placeholder="Smith" />
                  </FieldGroup>
                </Row>

                <FieldGroup>
                  <Label>Phone</Label>
                  <Input primary={primary} type="tel" value={form.phone} onChange={e => onFormChange('phone', e.target.value)} placeholder="+1 403 555 0100" />
                </FieldGroup>

                <FieldGroup>
                  <Label>Email</Label>
                  <Input primary={primary} type="email" value={form.email} onChange={e => onFormChange('email', e.target.value)} placeholder="jane@example.com" />
                </FieldGroup>

                <FieldGroup>
                  <Label>Special Requests</Label>
                  <Textarea primary={primary} rows={2} value={form.notes} onChange={e => onFormChange('notes', e.target.value)} placeholder="Allergies, celebrations, accessibility..." />
                </FieldGroup>

                <div style={{ fontSize: 12, color: '#999', marginBottom: 16 }}>
                  * Phone or email required for confirmation
                </div>

                <BtnRow>
                  <GhostBtn onClick={onBack}>Back</GhostBtn>
                  <PrimaryBtn
                    primary={primary}
                    style={{ width: 'auto', paddingLeft: 28, paddingRight: 28 }}
                    onClick={onSubmit}
                    disabled={loading || !form.first_name || !form.last_name}
                  >
                    {loading ? 'Booking...' : 'Confirm Booking'}
                  </PrimaryBtn>
                </BtnRow>
              </>
            )}

            {/* ── STEP 4: Confirmation ──────────────────────────────────── */}
            {step === STEPS.CONFIRM && confirmation && (
              <>
                <ConfirmCheck>&#10003;</ConfirmCheck>
                <ConfirmTitle>You're booked!</ConfirmTitle>
                <ConfirmSub>We'll see you soon. Check your phone or email for details.</ConfirmSub>

                <ConfirmBox>
                  <ConfirmDetail>
                    <ConfirmLabel>Name</ConfirmLabel>
                    <ConfirmValue>{confirmation.guest_name}</ConfirmValue>
                  </ConfirmDetail>
                  <ConfirmDetail>
                    <ConfirmLabel>Date</ConfirmLabel>
                    <ConfirmValue>{fmtDate(confirmation.date)}</ConfirmValue>
                  </ConfirmDetail>
                  <ConfirmDetail>
                    <ConfirmLabel>Time</ConfirmLabel>
                    <ConfirmValue>{fmtTime(confirmation.time)}</ConfirmValue>
                  </ConfirmDetail>
                  <ConfirmDetail>
                    <ConfirmLabel>Party</ConfirmLabel>
                    <ConfirmValue>{confirmation.party_size} guest{confirmation.party_size !== 1 ? 's' : ''}</ConfirmValue>
                  </ConfirmDetail>
                  {confirmation.notes && (
                    <ConfirmDetail>
                      <ConfirmLabel>Notes</ConfirmLabel>
                      <ConfirmValue style={{ maxWidth: '60%', textAlign: 'right' }}>{confirmation.notes}</ConfirmValue>
                    </ConfirmDetail>
                  )}
                  <ConfirmDetail>
                    <ConfirmLabel>Booking #</ConfirmLabel>
                    <ConfirmValue style={{ fontFamily: 'monospace' }}>ORD-{String(confirmation.id).padStart(5, '0')}</ConfirmValue>
                  </ConfirmDetail>
                </ConfirmBox>

                <PrimaryBtn primary={primary} onClick={onReset}>
                  Make Another Booking
                </PrimaryBtn>
              </>
            )}
          </CardBody>
        </Card>
      </Shell>
    </>
  );
};

export default BookingWidgetView;
